import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { useBrandProfile } from './brand-profile';
import { GenieChatContext, type GenieChatContextValue, type GenieMessage } from './genie-chat';
import { supabase } from '../lib/supabase';
import { useLabels } from '../config/useLabels';
import { getSessionId, analytics } from '../utils/analytics';

const STORAGE_PREFIX = 'bhcs_parent_genie:';

// A saved thread older than this is discarded on load: replaying weeks-old
// answers (stale balances, past due dates) as context misleads the model and
// the parent alike.
const THREAD_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

type PersistedThread = { messages: GenieMessage[]; conversationHistory: GenieMessage[]; savedAt: number };

function loadThread(key: string): PersistedThread | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedThread;
    if (!Array.isArray(parsed.messages) || !Array.isArray(parsed.conversationHistory)) return null;
    // Legacy payloads without savedAt are treated as expired.
    if (typeof parsed.savedAt !== 'number' || Date.now() - parsed.savedAt > THREAD_MAX_AGE_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

type ParentQueryResponse = {
  result?: { formatted?: string; needsClarification?: boolean };
  conversationHistory?: GenieMessage[];
  error?: string;
  message?: string;
};

/**
 * Consume the parent-query SSE stream: `status` events update the progress
 * indicator while the ReAct loop runs; the single `result` event carries the
 * same payload as the legacy JSON response.
 */
async function readSseResult(
  response: Response,
  onStatus: (s: { type?: string; iteration?: number }) => void,
): Promise<ParentQueryResponse> {
  if (!response.body) throw new Error('Streaming not supported.');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: ParentQueryResponse | null = null;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep = buffer.indexOf('\n\n');
    while (sep !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      sep = buffer.indexOf('\n\n');
      let event = 'message';
      let data = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event: ')) event = line.slice(7).trim();
        else if (line.startsWith('data: ')) data += line.slice(6);
      }
      if (!data) continue;
      try {
        const parsed = JSON.parse(data) as ParentQueryResponse & { type?: string; iteration?: number };
        if (event === 'status') onStatus(parsed);
        else if (event === 'result') result = parsed;
      } catch {
        // ignore malformed frames
      }
    }
  }
  if (!result) throw new Error('The connection dropped before the answer arrived.');
  return result;
}

/**
 * Holds the one Parent Genie conversation for the signed-in parent. Mounted in the parent
 * Layout, above both the `/assistant` page and the Assistant bubble.
 */
export function GenieChatProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const t = useLabels();
  const { activeBrand, isMultiBrand } = useBrandProfile();

  const [messages, setMessages] = useState<GenieMessage[]>([]);
  const [conversationHistory, setConversationHistory] = useState<GenieMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [lastQuestion, setLastQuestion] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const storageKey = user?.id ? `${STORAGE_PREFIX}${user.id}` : null;
  const hydratedRef = useRef(false);

  // Restore the saved thread for this user (persists across reloads on this device).
  useEffect(() => {
    if (!storageKey || hydratedRef.current) return;
    const saved = loadThread(storageKey);
    if (saved) {
      setMessages(saved.messages);
      setConversationHistory(saved.conversationHistory);
    }
    hydratedRef.current = true;
  }, [storageKey]);

  // Persist after each turn. Skip until hydration has run so we don't clobber saved state.
  useEffect(() => {
    if (!storageKey || !hydratedRef.current) return;
    try {
      if (messages.length === 0) localStorage.removeItem(storageKey);
      else localStorage.setItem(storageKey, JSON.stringify({ messages, conversationHistory, savedAt: Date.now() }));
    } catch {
      // ignore storage quota / private-mode errors
    }
  }, [storageKey, messages, conversationHistory]);

  const clearThread = useCallback(() => {
    setMessages([]);
    setConversationHistory([]);
    if (storageKey) {
      try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
    }
  }, [storageKey]);

  const send = useCallback(async (question: string) => {
    const trimmed = question.trim();
    if (!trimmed || abortRef.current) return;

    setMessages((prev) => [...prev, { role: 'user', text: trimmed }]);
    setLoading(true);
    setStatusText(t('genie.thinking'));
    setLastQuestion(trimmed);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) throw new Error('Please sign in again.');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) throw new Error('Missing configuration.');

      const response = await fetch(`${supabaseUrl}/functions/v1/parent-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Ask for live progress events; older function versions ignore this
          // and reply with plain JSON, which we still handle below.
          'Accept': 'text/event-stream',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({
          query: trimmed,
          conversationHistory,
          sessionId: getSessionId(),
          // The profile being viewed, so catalog answers match the catalog on screen. Sent
          // only in the multi-brand portal: without it the function assumes the single-brand
          // BHCS portal, which has no profile switcher for the assistant to mention.
          ...(isMultiBrand ? { brand: activeBrand } : {}),
        }),
        signal: controller.signal,
      });

      let data: ParentQueryResponse;
      const contentType = response.headers?.get?.('content-type') ?? '';
      if (response.ok && contentType.includes('text/event-stream')) {
        data = await readSseResult(response, (s) => {
          if (s.type === 'querying') setStatusText(t('genie.status.querying'));
          else setStatusText((s.iteration ?? 1) > 1 ? t('genie.status.digging') : t('genie.status.thinking'));
        });
        // Errors after the stream opened ride inside the result event.
        if (data.error) throw new Error(data.message || data.error);
      } else {
        const responseText = await response.text();
        if (!response.ok) {
          let msg = `Something went wrong (HTTP ${response.status}).`;
          try {
            const body = JSON.parse(responseText) as ParentQueryResponse;
            if (response.status === 429 || response.status === 503) {
              msg = body.message || 'The assistant is busy right now. Please try again shortly.';
            } else if (body.message || body.error) {
              msg = body.message || body.error || msg;
            }
          } catch {
            // keep default message
          }
          throw new Error(msg);
        }
        data = JSON.parse(responseText) as ParentQueryResponse;
      }

      const answer = data.result?.formatted || "I couldn't find an answer to that.";
      setMessages((prev) => [...prev, { role: 'assistant', text: answer }]);
      if (Array.isArray(data.conversationHistory)) {
        setConversationHistory(data.conversationHistory);
      }
    } catch (err) {
      const cancelled = err instanceof DOMException && err.name === 'AbortError';
      const msg = cancelled ? t('genie.cancelled') : err instanceof Error ? err.message : 'Something went wrong.';
      setMessages((prev) => [...prev, { role: 'assistant', text: msg, isError: true }]);
    } finally {
      abortRef.current = null;
      setLoading(false);
    }
  }, [t, conversationHistory, isMultiBrand, activeBrand]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  // Record a 1-3 star helpfulness rating for an assistant answer.
  const rate = useCallback((index: number, rating: number) => {
    const answer = messages[index]?.text ?? '';
    // The question is the user turn immediately preceding this answer.
    const question = [...messages.slice(0, index)].reverse().find((m) => m.role === 'user')?.text ?? '';
    setMessages((prev) => prev.map((m, i) => (i === index ? { ...m, rating } : m)));
    analytics.trackAgentFeedback({ user, rating, query: question, answer });
  }, [messages, user]);

  const openPanel = useCallback((source: string) => {
    setPanelOpen(true);
    analytics.trackAgentView({ user, source });
  }, [user]);

  const closePanel = useCallback(() => setPanelOpen(false), []);

  const value = useMemo<GenieChatContextValue>(() => ({
    messages,
    loading,
    statusText,
    lastQuestion,
    send,
    stop,
    clearThread,
    rate,
    panelOpen,
    openPanel,
    closePanel,
  }), [messages, loading, statusText, lastQuestion, send, stop, clearThread, rate, panelOpen, openPanel, closePanel]);

  return <GenieChatContext.Provider value={value}>{children}</GenieChatContext.Provider>;
}
